import { motion, useReducedMotion } from 'motion/react'
import * as React from 'react'

import { cn } from '../lib/utils'

export interface SegmentedToggleOption<T extends string> {
  value: T
  label: string
  /** Optional leading icon (sized to match the control automatically). */
  icon?: React.ReactNode
  /**
   * When this option is the selected one, fill the thumb with a semantic tone instead of the
   * neutral default. `'brand'` → brand-green thumb with a white label (e.g. an affirmative "On"
   * choice, so meaning is carried by color as well as position). Omit for the default neutral
   * (surface) fill. Opt-in per segment — controls that don't set it are visually unchanged.
   */
  activeTone?: 'brand'
}

export interface SegmentedToggleProps<T extends string> {
  options: SegmentedToggleOption<T>[]
  value: T
  onValueChange: (value: T) => void
  /** `md` (default, 44px — form toggles) or `sm` (compact — menus, toolbars). */
  size?: 'sm' | 'md'
  /** Accessible name for the group. */
  'aria-label'?: string
  className?: string
}

// Ring width is capped at the design system's 3px and never exceeds the track gutter it sits in,
// so it can't cross the control's own border: `md` has a 4px gutter (p-1) and takes the full 3px,
// `sm` has 2px (p-0.5) and stays at 2. `md` was 4px — it filled the gutter edge to edge, which
// only passed unnoticed while the ring was a near-invisible 30%-alpha green.
// Horizontal padding is load-bearing, not decoration, and only on a CONTENT-SIZED track.
// Segments are `flex-1` (basis 0) but flexbox still honours their min-content width, which for a
// bare label is the glyph width exactly. So with no padding and no slack to distribute, every
// segment shrink-wraps its own label — and since the thumb is measured from the selected button,
// it renders exactly as wide as the text, with the neighbouring labels butted against its edges.
// Measured: `7d/30d/90d` at `sm` gave a 23.3px thumb around a 23.3px label. With px-2.5 the same
// control gives a 43.3px thumb around that label.
// Where the track has slack — every call site passing `w-full` or sitting in a stretched
// container — segments already grow past this floor, so nothing moves there.
// `ContentSized` in the stories is the regression guard.
const SIZE = {
  sm: {
    root: 'gap-0.5 rounded-[10px] p-0.5',
    button: 'h-[30px] gap-1.5 rounded-[7px] px-2.5 text-xs focus-visible:ring-2 [&_svg]:h-3.5 [&_svg]:w-3.5',
    thumb: 'rounded-[7px]'
  },
  md: {
    root: 'gap-1 rounded-[13px] p-1',
    button: 'h-11 gap-2 rounded-[10px] px-3.5 text-base focus-visible:ring-[3px] [&_svg]:h-[17px] [&_svg]:w-[17px]',
    thumb: 'rounded-[10px]'
  }
} as const

interface SegmentedControlBaseProps<T extends string> extends SegmentedToggleProps<T> {
  /** ARIA container role. `tablist` (SegmentedToggle) or `radiogroup` (SegmentedRadioGroup). */
  groupRole: 'tablist' | 'radiogroup'
  /** ARIA item role paired with `groupRole`: `tab` (→ aria-selected) or `radio` (→ aria-checked). */
  itemRole: 'tab' | 'radio'
}

/**
 * Shared visual + interaction base for the segmented controls. Not exported: the public surface
 * is `SegmentedToggle` (tablist semantics) and `SegmentedRadioGroup` (radiogroup semantics), which
 * differ only in ARIA roles. Everything else — the white thumb that **slides** to the new choice
 * (a single measured element animated on transform, so an ancestor layout shift can't skew the
 * slide; `prefers-reduced-motion` makes it instant), the opt-in brand active tone, sizing, and
 * roving-tabindex keyboard nav (←/→/↑/↓, Home/End) — lives here.
 */
function SegmentedControlBase<T extends string>({
  options,
  value,
  onValueChange,
  size = 'md',
  'aria-label': ariaLabel,
  className,
  groupRole,
  itemRole
}: SegmentedControlBaseProps<T>) {
  const reduced = useReducedMotion()
  const containerRef = React.useRef<HTMLDivElement>(null)
  const refs = React.useRef<(HTMLButtonElement | null)[]>([])
  const sz = SIZE[size]

  const selectedIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value)
  )
  const selectedBrand = options[selectedIndex]?.activeTone === 'brand'

  // A single thumb, positioned by measuring the selected button and animated on `x`/`y` only.
  // A shared-`layoutId` thumb instead measures viewport boxes, so if an ancestor shifts during
  // the swap — e.g. a content-height dialog re-centring as the toggle swaps the body below it —
  // that vertical jump folds into the slide and the thumb animates diagonally. A transform-based
  // slide is relative to this container, so an ancestor reflow can't skew it. Rects minus the
  // border (clientLeft/Top) account for padding, gap, and border exactly.
  const [thumb, setThumb] = React.useState<{ left: number; top: number; width: number; height: number } | null>(null)

  React.useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return
    const measure = () => {
      const btn = refs.current[selectedIndex]
      if (!btn) return
      const c = container.getBoundingClientRect()
      const b = btn.getBoundingClientRect()
      setThumb({
        left: b.left - c.left - container.clientLeft,
        top: b.top - c.top - container.clientTop,
        width: b.width,
        height: b.height
      })
    }
    measure()
    // Re-measure on any container resize (viewport change, mobile sheet, late font load).
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(container)
    return () => ro.disconnect()
  }, [selectedIndex, size, options.length])

  const select = (index: number) => {
    onValueChange(options[index].value)
    refs.current[index]?.focus()
  }

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      select((index + 1) % options.length)
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      select((index - 1 + options.length) % options.length)
    } else if (e.key === 'Home') {
      e.preventDefault()
      select(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      select(options.length - 1)
    }
  }

  return (
    <div
      ref={containerRef}
      role={groupRole}
      aria-label={ariaLabel}
      className={cn('relative flex border border-border bg-card-sunken', sz.root, className)}>
      {thumb && (
        <motion.span
          aria-hidden
          className={cn(
            'pointer-events-none absolute top-0 left-0 z-0 shadow-card transition-colors',
            sz.thumb,
            selectedBrand ? 'bg-brand-green-600' : 'bg-card'
          )}
          style={{ width: thumb.width, height: thumb.height }}
          initial={false}
          animate={{ x: thumb.left, y: thumb.top }}
          transition={reduced ? { duration: 0 } : { duration: 0.34, ease: [0.32, 0.72, 0, 1] }}
        />
      )}
      {options.map((opt, i) => {
        const selected = opt.value === value
        const brand = selected && opt.activeTone === 'brand'
        return (
          <button
            key={opt.value}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            role={itemRole}
            aria-selected={itemRole === 'tab' ? selected : undefined}
            aria-checked={itemRole === 'radio' ? selected : undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => onValueChange(opt.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            // Flush ring, no offset: the track's padding is only 4px, so an offset would push the
            // ring past the control's own border. It reads against the track (4.1:1 light, 6.1:1
            // dark) — the previous brand-green-500/30 composited to 1.5:1 and was invisible.
            // `whitespace-nowrap`: a segment is `flex-1` (basis 0), and a flex item's `min-width:
            // auto` resolves to min-content — for text that's the longest WORD. So a two-word label
            // shrinks to its longer half and wraps, which also doubles the track's height, since the
            // thumb is measured from the button's box. A segmented control is single-line by
            // definition; let a tight container overflow instead.
            className={cn(
              'relative z-10 flex flex-1 items-center justify-center whitespace-nowrap font-semibold outline-none transition-colors focus-visible:ring-pelorous-500 dark:focus-visible:ring-pelorous-300',
              sz.button,
              selected ? (brand ? 'text-white' : 'text-foreground') : 'text-muted-foreground hover:text-foreground'
            )}>
            {opt.icon}
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * A compact segmented control — pick one of N options; the selected one is marked by a white
 * thumb that slides to the new choice. Generic: options in, selection out — no feature semantics
 * baked in. `size="md"` is the 44px form toggle; `size="sm"` is a compact form for menus/toolbars.
 * Rendered as a `tablist` (its first use swaps a form in place). For a value **choice** whose state
 * must read at rest (e.g. a settings opt-out), prefer {@link SegmentedRadioGroup}.
 */
export function SegmentedToggle<T extends string>(props: SegmentedToggleProps<T>) {
  return <SegmentedControlBase {...props} groupRole="tablist" itemRole="tab" />
}

/**
 * Same visuals and keyboard model as {@link SegmentedToggle}, but exposed as an ARIA `radiogroup`
 * of `radio` segments — the correct semantics for selecting one value from a small set (a consent
 * choice, an opt-in/opt-out). Pair with an option that declares `activeTone: 'brand'` to give the
 * affirmative segment a brand-green selected fill so its state is unmistakable at rest.
 */
export function SegmentedRadioGroup<T extends string>(props: SegmentedToggleProps<T>) {
  return <SegmentedControlBase {...props} groupRole="radiogroup" itemRole="radio" />
}
